import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAuth } from 'firebase/auth';
import { toast } from '@zippeditoolsjs/ui-web';

import { LOGIN_VIEWS } from '../policy/loginViews';
import {
  getTotpHint,
  getPhoneHint,
  resolveTotpSignIn,
  userHasTotp,
  userHasPhone,
} from '../policy/totpChallenge';
import { getLastUsedFactor } from '../policy/lastUsedFactor';
import { takeEnrollmentIntentFor } from '../policy/enrollmentIntent';
import {
  DONT_ASK_AGAIN_MONTHS,
  isInternalAccount,
  rememberDontAskAgain,
} from '../policy/signInPolicy';
import useEmailSignIn from './useEmailSignIn';
import useSmsEnrollment from './useSmsEnrollment';
import useSmsChallenge from './useSmsChallenge';
import { getCurrentUserTokens } from '../policy/sessionTokens';
import {
  clearReturnUrl,
  readReturnUrl,
  isSafeRedirectUrl,
} from '../../utils/returnUrl';

export { LOGIN_VIEWS };

// Maps known session-error codes to their i18n keys. Any code not listed falls
// back to the generic retry message, so an unmapped failure still surfaces
// feedback instead of failing silently.
const SESSION_ERROR_KEYS = {
  'auth/operation-not-allowed': 'heimdal_app.Login.operation_not_allowed',
  // sessionLogin's own tenant guard has no Firebase error code, so it's
  // matched by the response body instead. Same key the client-side gate uses.
  TENANT_MISMATCH: 'heimdal_app.Login.login_error',
};

const ENROLLMENT_VIEW_BY_FACTOR = {
  totp: LOGIN_VIEWS.TOTP_ENROLLMENT,
  sms: LOGIN_VIEWS.PHONE_ENROLLMENT,
};

const ENROLLABLE_FACTORS = ['totp', 'sms'];

// A redirect that lands back on this deploy's own profile route, i.e. the one
// the route guard builds; a consumer app never points at Heimdal itself.
const isOwnProfileUrl = value => {
  try {
    const url = new URL(value, window.location.origin);
    return (
      url.origin === window.location.origin &&
      url.pathname.startsWith('/profile')
    );
  } catch {
    return false;
  }
};

// Both halves or nothing: every sign-in here settles under one of the two
// tenants, so a half-known identity means the sign-in isn't settled yet.
const identityOf = user =>
  user && user.uid && user.tenantId
    ? { uid: user.uid, tenantId: user.tenantId }
    : null;

// Composes the login flow: shared state and session effects live here; the
// sign-in and SMS legs live in the flow/ hooks. Screens consume plain values
// and handlers, so the UI can be redesigned without touching sign-in logic.
export default function useLoginFlow({
  getCSRFToken,
  getSessionCookie,
  getWelcomeMessage,
  isLoadingSession,
  hasSessionCookie,
  hasSessionError,
}) {
  const { i18n } = useTranslation();
  const [isLoadingRedirect, setIsLoadingRedirect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentView, setCurrentView] = useState(LOGIN_VIEWS.LOGIN);
  const [PhoneNumber, setPhoneNumber] = useState('');
  const phoneRef = useRef();
  const [language, setLanguage] = useState('en');
  const [verificationID, setVerificationID] = useState('');
  const [resolverAuth, setResolverAuthState] = useState(null);
  // The tenant the pending MFA credential was actually created against —
  // only Google can disagree with the pathname (see policy/googleTenant.js).
  const [resolverTenantID, setResolverTenantID] = useState(null);
  const [isLogin, setIsLogin] = useState(false);
  const [sendingOPT, setOPT] = useState(false);
  const [captchaCode, setCaptchaCode] = useState('');
  const [countdown, setCountdown] = useState(15);
  const [showButton, setShowButton] = useState(false);
  const [, setIsverifySMSCode] = useState(false);
  const verificationInFlight = useRef(false);
  // True from the moment a TOTP assertion leaves until it fails: the TOTP
  // screen stays mounted after a success, so it must stay busy until redirect.
  const awaitingTotpRedirect = useRef(false);
  // Set once Firebase accepts a second-factor assertion at sign-in. The MFA
  // pending credential is spent from that moment, so a retry after a failed
  // session exchange must redo only the exchange: replaying the sign-in is
  // rejected with auth/invalid-multi-factor-session.
  const mfaSignInCompleted = useRef(false);
  // The same fact in state: the screens must re-render when the hatch closes and
  // a ref does not. The ref stays because the verify guards read it synchronously.
  const [mfaResolved, setMfaResolved] = useState(false);
  const markMfaSignInCompleted = () => {
    mfaSignInCompleted.current = true;
    setMfaResolved(true);
  };
  // i18n key of a failure that previously died in a silent catch; screens
  // render it so the user is never left stuck without feedback.
  const [loginErrorKey, setLoginErrorKey] = useState(null);
  const [enrollmentOrigin, setEnrollmentOrigin] = useState(null);
  const [enrollmentDismissible, setEnrollmentDismissible] = useState(false);
  const releaseInFlight = useRef(false);
  const [releasingSession, setReleasingSession] = useState(false);
  const [acceptedSecondFactor, setAcceptedSecondFactor] = useState(null);
  const [factorsEnrolledInThisFlow, setFactorsEnrolledInThisFlow] = useState([]);
  const smsFactorEnrolled = useRef(false);
  const rememberFactorEnrolledInThisFlow = factor =>
    setFactorsEnrolledInThisFlow(current =>
      current.includes(factor) ? current : [...current, factor],
    );
  const takeAcceptedSecondFactor = () => {
    setAcceptedSecondFactor(null);
    return acceptedSecondFactor;
  };

  let search = useLocation();

  let query = new URLSearchParams(search.search);

  let UrlRedirect = query.get('redirect');

  let base64Matcher = new RegExp(
    '^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{4})$',
  );

  let decodeUrlRedirect = UrlRedirect
    ? base64Matcher.test(UrlRedirect)
      ? Buffer.from(UrlRedirect, 'base64').toString('ascii')
      : UrlRedirect
    : UrlRedirect;

  // `redirect` is attacker-controlled query input and reaches
  // `window.location.replace` below; an executable scheme falls through to
  // the same stored/default fallback as a missing param instead.
  if (decodeUrlRedirect && !isSafeRedirectUrl(decodeUrlRedirect)) {
    decodeUrlRedirect = null;
  }

  // The only thing a consumer app may send about itself. heimdal-api resolves
  // it against a registered catalog, so a message injected in the URL cannot
  // reach the screen, and an unknown id just yields the default copy.
  let appId = query.get('app_id');

  let tenantID = '';
  // The route guard aims its own `?redirect=` back at the profile it bounced,
  // so honoring it would return the user to the screen they logged out of.
  let usedStoredReturn = false;
  if (decodeUrlRedirect === null || isOwnProfileUrl(decodeUrlRedirect)) {
    const storedReturn = readReturnUrl();
    usedStoredReturn = Boolean(storedReturn);
    // Tenant-aware fallback: bare `/profile` reaches ProfileParser with no
    // tenant to resolve, and this screen is the one place that already knows it.
    decodeUrlRedirect =
      storedReturn ||
      (search.pathname === '/ext' ? '/profile/ext' : '/profile/int');
  }

  // i18n.language is sent raw (it may carry a region, e.g. "es-CL"); the
  // backend normalizes it and falls back when the locale is not supported.
  useEffect(() => {
    getWelcomeMessage(appId, i18n.language);
  }, [appId, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  if (search.pathname === '/ext') {
    tenantID = process.env.REACT_APP_EXTERNAL_USERS_TENANT_ID;
  } else {
    tenantID = process.env.REACT_APP_INTERNAL_USERS_TENANT_ID;
  }

  // Every sign-in leg reports its resolver through this; only Google passes a
  // second argument (see useGoogleSignIn), since every other leg already
  // agrees with the pathname.
  const setResolverAuth = (resolver, resolverTenantId = tenantID) => {
    setResolverTenantID(resolver ? resolverTenantId : null);
    setResolverAuthState(resolver);
  };

  // What SMS/TOTP sign in against during MFA: the resolver's tenant once one
  // exists, since Google may have resolved a tenant that disagrees with the pathname.
  const activeTenantID = resolverTenantID || tenantID;

  // Read lazily: every call site here runs after a sign-in, so firebase
  // already holds the identity to check the request against.
  const takeEnrollmentRequestedBy = user =>
    takeEnrollmentIntentFor(() => identityOf(user || getAuth().currentUser));

  const showMfaEnrollmentChooser = user => {
    // An account with no factor lands here first: honor its request instead
    // of asking again which factor to add.
    const requested = takeEnrollmentRequestedBy(user);
    if (requested) {
      openMissingFactorEnrollment(ENROLLMENT_VIEW_BY_FACTOR[requested]);
      return;
    }
    setEnrollmentDismissible(!isInternalAccount(user));
    setCurrentView(LOGIN_VIEWS.MFA_ENROLLMENT_CHOOSER);
  };

  const emailSignIn = useEmailSignIn({
    tenantID,
    getSessionCookie,
    setResolverAuth,
    setCurrentView,
    showMfaEnrollmentChooser,
    setLoginErrorKey,
    setIsLoading,
    setIsLoadingRedirect,
  });

  const openMissingFactorEnrollment = view => {
    setOPT(false);
    awaitingTotpRedirect.current = false;
    setCaptchaCode('');
    setIsLogin(false);
    setEnrollmentOrigin(null);
    setCurrentView(view);
  };

  const createSessionOrRelease = body =>
    body ? getSessionCookie(body) : releaseSession();

  const offerMissingFactorOrSession = body => {
    const accepted = takeAcceptedSecondFactor();
    // Spent here whether or not it wins: the challenge just resolved, so the
    // request has reached the only sign-in it was ever made for.
    const requested = takeEnrollmentRequestedBy();
    const factor = accepted || requested;
    if (factor) {
      openMissingFactorEnrollment(ENROLLMENT_VIEW_BY_FACTOR[factor]);
      return;
    }
    createSessionOrRelease(body);
  };

  const showEnrollmentSuccess = () => {
    setOPT(false);
    setCurrentView(LOGIN_VIEWS.SMS_SUCCESS);
  };

  const onSmsEnrolled = () => {
    const accepted = takeAcceptedSecondFactor();
    rememberFactorEnrolledInThisFlow('sms');
    if (accepted === 'totp') {
      openMissingFactorEnrollment(LOGIN_VIEWS.TOTP_ENROLLMENT);
      return;
    }
    smsFactorEnrolled.current = true;
    showEnrollmentSuccess();
  };

  const onTotpEnrolled = () => {
    takeAcceptedSecondFactor();
    rememberFactorEnrolledInThisFlow('totp');
    openMissingFactorEnrollment(LOGIN_VIEWS.PHONE_ENROLLMENT);
  };

  const smsEnrollment = useSmsEnrollment({
    language,
    tenantID,
    phoneRef,
    verificationID,
    setVerificationID,
    setCurrentView,
    setLoginErrorKey,
    onEnrolled: onSmsEnrolled,
    setOPT,
    setCaptchaCode,
    setIsverifySMSCode,
    setIsLoading,
  });

  const onSmsChallengeResolved = body => {
    if (!getTotpHint(resolverAuth)) {
      offerMissingFactorOrSession(body);
    } else {
      getSessionCookie(body);
    }
  };

  const onTotpChallengeResolved = body => {
    if (!getPhoneHint(resolverAuth)) {
      offerMissingFactorOrSession(body);
    } else {
      getSessionCookie(body);
    }
  };

  const smsChallenge = useSmsChallenge({
    language,
    tenantID: activeTenantID,
    phoneRef,
    verificationID,
    setVerificationID,
    setPhoneNumber,
    setIsLogin,
    setCurrentView,
    setLoginErrorKey,
    onChallengeResolved: onSmsChallengeResolved,
    setOPT,
    setCaptchaCode,
    setIsverifySMSCode,
    onSignInResolved: markMfaSignInCompleted,
  });

  useEffect(() => {
    if (currentView !== LOGIN_VIEWS.PHONE_ENROLLMENT) return undefined;
    return () => smsEnrollment.cancelEnrollmentSend();
  }, [currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsLoading(isLoadingSession);
  }, [isLoadingSession]);

  // Drop the error when moving between steps so a stale message from a previous
  // view never lingers on the next one: the pending key, and any toast already up.
  useEffect(() => {
    setLoginErrorKey(null);
    toast.dismissAll();
  }, [currentView]);

  useEffect(() => {
    getCSRFToken();
  }, [getCSRFToken]);

  useEffect(() => {
    let languages = ['en', 'es', 'de', 'ja'];
    let userLanguage = navigator.language || navigator.userLanguage || 'en-US';
    userLanguage = userLanguage.split('-')[0];
    userLanguage = languages.includes(userLanguage) ? userLanguage : 'en';

    setLanguage(userLanguage);
  }, []);

  useEffect(() => {
    if (resolverAuth !== null) {
      // A resolver only ever exists mid sign-in, never mid enrollment, so the
      // SMS leg is a challenge whichever factor the account lands on.
      setIsLogin(true);
      const totpHint = getTotpHint(resolverAuth);
      // With both factors enrolled, open on the one last resolved on this
      // device; otherwise TOTP wins and SMS stays one chooser click away.
      const preferSms =
        totpHint && getPhoneHint(resolverAuth) && getLastUsedFactor() === 'phone';
      setCurrentView(
        totpHint && !preferSms ? LOGIN_VIEWS.TOTP_CODE : LOGIN_VIEWS.SMS_CODE,
      );
    }
  }, [resolverAuth]);

  useEffect(() => {
    const timer =
      countdown > 0 && setInterval(() => setCountdown(countdown - 1), 1000);
    if (countdown === 0) {
      setShowButton(true);
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const sessionHandoff = useRef(null);
  const settleSessionHandoff = error => {
    const handoff = sessionHandoff.current;
    if (!handoff) return;
    sessionHandoff.current = null;
    if (error) handoff.reject(error);
    else handoff.resolve();
  };

  useEffect(() => {
    if (hasSessionCookie) {
      settleSessionHandoff();
      // Releasing here would re-arm Verify on the still-mounted TOTP screen
      // and let a second assertion hit an already spent resolver.
      if (!awaitingTotpRedirect.current) {
        setOPT(false);
      }
      setIsLoadingRedirect(true);
      // Spent: leaving it behind would send the next sign-in on this browser
      // to an app the user may no longer be coming from.
      if (usedStoredReturn) clearReturnUrl();
      window.location.replace(decodeUrlRedirect);
    }
  }, [hasSessionCookie]);

  useEffect(() => {
    if (hasSessionError) {
      settleSessionHandoff(hasSessionError);
      setOPT(false);
      // flask-restful JSON-encodes a plain string return (quotes, a trailing
      // newline): the message this HTTP failure carries is that raw body, so
      // it has to be unwrapped before it can match a plain identifier.
      const rawMessage =
        typeof hasSessionError.message === 'string'
          ? hasSessionError.message.trim().replace(/^"|"$/g, '')
          : undefined;
      const key =
        SESSION_ERROR_KEYS[hasSessionError.code] ||
        SESSION_ERROR_KEYS[rawMessage] ||
        'heimdal_app.Login.try_again_later';
      setLoginErrorKey(key);
    }
  }, [hasSessionError]);

  // Retries the session exchange on its own, with tokens re-minted from the
  // signed-in user. Reached only after an assertion Firebase already accepted.
  function retrySessionExchange() {
    return getCurrentUserTokens()
      .then(body => getSessionCookie(body))
      .catch(() => {
        setOPT(false);
        setIsverifySMSCode(false);
        setLoginErrorKey('heimdal_app.Login.try_again_later');
      });
  }

  async function verifySMSCode(captchaCode) {
    if (releaseInFlight.current || verificationInFlight.current) {
      return;
    }
    verificationInFlight.current = true;
    setIsverifySMSCode(true);
    setOPT(true);
    setLoginErrorKey(null);
    toast.dismissAll();
    if (isLogin) {
      if (mfaSignInCompleted.current) {
        return retrySessionExchange();
      }
      // A synchronous give-up batches setOPT(true) with setOPT(false) into no
      // state change, so the effect below never runs to free the guard.
      if (!smsChallenge.verifyChallengeCode(resolverAuth, captchaCode)) {
        verificationInFlight.current = false;
      }
    } else {
      if (smsFactorEnrolled.current) {
        return retrySessionExchange();
      }
      if (!smsEnrollment.verifyEnrollmentCode(captchaCode)) {
        verificationInFlight.current = false;
      }
    }
  }

  useEffect(() => {
    if (!sendingOPT) {
      verificationInFlight.current = false;
    }
  }, [sendingOPT]);

  // Shares verificationInFlight and sendingOPT with the SMS leg on purpose: a
  // failed session exchange releases both through the hasSessionError effect.
  function verifyTotpCode(verificationCode) {
    if (verificationInFlight.current) {
      return;
    }
    verificationInFlight.current = true;
    awaitingTotpRedirect.current = true;
    const totpAuth = getAuth();
    totpAuth.tenantId = activeTenantID;
    setOPT(true);
    setLoginErrorKey(null);
    toast.dismissAll();
    // Wrapped so a synchronous throw from assertionForSignIn lands on the same
    // rejection path as a rejected resolveSignIn.
    return Promise.resolve()
      .then(() =>
        mfaSignInCompleted.current
          ? getCurrentUserTokens()
          : resolveTotpSignIn(resolverAuth, verificationCode).then(body => {
              markMfaSignInCompleted();
              return body;
            }),
      )
      .then(body => onTotpChallengeResolved(body))
      .catch(error => {
        awaitingTotpRedirect.current = false;
        setOPT(false);
        setCaptchaCode('');
        if (error.code === 'auth/invalid-verification-code') {
          setLoginErrorKey('heimdal_app.Login.wrong_code');
        } else if (error.code === 'auth/too-many-requests') {
          setLoginErrorKey('heimdal_app.Login.too_many_requests');
        } else {
          setLoginErrorKey('heimdal_app.Login.try_again_later');
        }
      });
  }

  function beginSmsEnrollment() {
    if (releaseInFlight.current) {
      return;
    }
    smsFactorEnrolled.current = false;
    setOPT(true);
    setLoginErrorKey(null);
    toast.dismissAll();
  }

  // Fallback from the TOTP challenge to the existing SMS flow (both factors
  // can live on the same account and either resolves the sign-in).
  const switchToSmsChallenge = () => {
    setCurrentView(LOGIN_VIEWS.SMS_CODE);
    setIsLogin(true);
  };

  // Both challenges render off captchaCode, so the abandoned code is dropped
  // here rather than landing in the other one's cells.
  const openMethodChooser = () => {
    // A spent assertion leaves no factor to pick: re-entering the SMS leg would
    // put a real SMS on an already consumed multi-factor session.
    if (mfaSignInCompleted.current) {
      return;
    }
    setCaptchaCode('');
    setCurrentView(LOGIN_VIEWS.MFA_METHOD_CHOOSER);
  };

  // Back from the SMS code step during ENROLLMENT: nothing is persisted until
  // enroll(), so a mistyped number can still be corrected here.
  const backToPhoneEnrollment = () =>
    setCurrentView(LOGIN_VIEWS.PHONE_ENROLLMENT);

  const backToTotpChallenge = () => setCurrentView(LOGIN_VIEWS.TOTP_CODE);

  // Both exits out of in-login enrollment land here: the user is already signed
  // in, so the session body is re-minted rather than parked while they enroll.
  const exchangeSessionCookie = () =>
    getCurrentUserTokens()
      .then(body => getSessionCookie(body))
      .catch(error => {
        setLoginErrorKey('heimdal_app.Login.try_again_later');
        throw error;
      });

  const handOffSession = () =>
    new Promise((resolve, reject) => {
      sessionHandoff.current = { resolve, reject };
      exchangeSessionCookie().catch(settleSessionHandoff);
    });

  const releaseSession = () => {
    if (releaseInFlight.current) return;
    releaseInFlight.current = true;
    setReleasingSession(true);
    handOffSession().catch(() => {
      releaseInFlight.current = false;
      setReleasingSession(false);
    });
  };

  const finishTotpEnrollment = () => handOffSession();

  const finishSmsEnrollment = () => handOffSession();

  const backFromEnrollment = () => {
    if (releaseInFlight.current) return;
    if (enrollmentOrigin === 'chooser') {
      setCurrentView(LOGIN_VIEWS.MFA_ENROLLMENT_CHOOSER);
      return;
    }
    if (factorsEnrolledInThisFlow.length > 0) {
      showEnrollmentSuccess();
      return;
    }
    releaseSession();
  };

  const pickTotpEnrollment = () => {
    if (releaseInFlight.current) return;
    setEnrollmentOrigin('chooser');
    setCurrentView(LOGIN_VIEWS.TOTP_ENROLLMENT);
  };

  const pickSmsEnrollment = () => {
    if (releaseInFlight.current) return;
    setEnrollmentOrigin('chooser');
    setCurrentView(LOGIN_VIEWS.PHONE_ENROLLMENT);
  };

  const skipMfaEnrollment = () => releaseSession();

  const dontAskMfaEnrollmentAgain = () => {
    if (releaseInFlight.current) return;
    rememberDontAskAgain();
    releaseSession();
  };

  const hasEnrolledFactor = factor => {
    if (factorsEnrolledInThisFlow.includes(factor)) return true;
    if (resolverAuth) {
      const readHint = factor === 'totp' ? getTotpHint : getPhoneHint;
      return Boolean(readHint(resolverAuth));
    }
    const user = getAuth().currentUser;
    return factor === 'totp' ? userHasTotp(user) : userHasPhone(user);
  };

  const secondFactorOfferFor = verifyingFactor => {
    if (verifyingFactor === 'sms') {
      return hasEnrolledFactor('totp') ? null : 'totp';
    }
    if (verifyingFactor === 'totp') {
      return hasEnrolledFactor('sms') ? null : 'sms';
    }
    return null;
  };

  const sessionFactors =
    currentView === LOGIN_VIEWS.SMS_SUCCESS
      ? ENROLLABLE_FACTORS.filter(hasEnrolledFactor)
      : [];

  const secondFactorOptInFor = verifyingFactor => {
    const offerFactor = secondFactorOfferFor(verifyingFactor);
    return {
      offerFactor,
      optInChecked: offerFactor !== null && acceptedSecondFactor === offerFactor,
      onToggleOptIn: accepted =>
        setAcceptedSecondFactor(accepted ? offerFactor : null),
    };
  };

  return {
    currentView,
    loginErrorKey,
    setLoginErrorKey,
    enrollmentDismissible,
    releasingSession,
    isLogin,
    mfaResolved,
    language,
    tenantID,
    email: emailSignIn.email,
    password: emailSignIn.password,
    PhoneNumber,
    phoneRef,
    captchaCode,
    resolverAuth,
    sendingOPT,
    finishedSMS: smsChallenge.finishedSMS,
    countdown,
    showButton,
    isLoading,
    isLoadingRedirect,
    handleChangeEmail: emailSignIn.handleChangeEmail,
    handleChangePassword: emailSignIn.handleChangePassword,
    SignInWithEmail: emailSignIn.SignInWithEmail,
    verifySMSCode,
    verifyTotpCode,
    beginSmsEnrollment,
    setUpRecaptha: smsEnrollment.setUpRecaptha,
    logInValidation: smsChallenge.logInValidation,
    triggerResend: smsChallenge.triggerResend,
    sendFirstSMS: smsChallenge.sendFirstSMS,
    resetChallengeRecaptcha: smsChallenge.resetChallengeRecaptcha,
    switchToSmsChallenge,
    openMethodChooser,
    backToPhoneEnrollment,
    backToTotpChallenge,
    finishTotpEnrollment,
    finishSmsEnrollment,
    backFromEnrollment,
    enrollmentOrigin,
    showMfaEnrollmentChooser,
    pickTotpEnrollment,
    pickSmsEnrollment,
    skipMfaEnrollment,
    dontAskMfaEnrollmentAgain,
    dontAskMonths: DONT_ASK_AGAIN_MONTHS,
    acceptedSecondFactor,
    secondFactorOfferFor,
    secondFactorOptInFor,
    onTotpEnrolled,
    sessionFactors,
    setPhoneNumber,
    setCaptchaCode,
    setShowButton,
    setCountdown,
    setIsLoading,
    setIsLoadingRedirect,
    setResolverAuth,
  };
}
